import React, { useEffect } from "react"
import DataTable from "../../../../../components/atoms/table/DataTable.atom"
import Column from "../../../../../components/atoms/table/Column.atom"
import useThemeContext from "../../../../../hooks/contextConsumers/useThemeContext.hook"
import Chip from "../../../../../components/atoms/misc/Chip.atom"
import ClientProjectChip from "../../../../../components/molecules/ClientProjectChip.molecule"
import { formatHours } from "../../../../../utils/time.utils"
import Tooltip from "../../../../../components/atoms/Tooltip.atom"
import PrimaryButton from "../../../../../components/molecules/buttons/PrimaryButton.molecule"
import { InputCheckbox } from "../../../../../components/atoms/InputCheckbox.atom"
import { clsx } from "../../../../../utils/strings.utils"
import TaskAdminInteractionChip, {
  TASK_ADMIN_INTERACTION_CHIP_TYPE,
} from "../../../../../components/molecules/TaskAdminInteractionChip.molecule"
import useTimeRegisterContext from "../../../hooks/useTimeRegisterContext.hook"
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined"
import {getDateTime, isSameDay} from "../../../../../utils/dateTime.utils"

const TaskList = ({
  dailyTasks,
  handleItemDrop,
  isDayEditable = true,
  setSelectedTask,
  contextMenuRef: cm,
  messageProps,
  onClickBeginEditingNewTaskName,
  selectedTasks,
  onClickSelectCheckbox,
  multiSelectCMRef,
  lastWorkDay,
  selectedDate,
  loading,
}) => {
  const { daysOff } = useTimeRegisterContext()
  const isSelectingTasks = selectedTasks.length > 0
  const { theme } = useThemeContext()

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--fill-drop-area-task-list",
      "20px"
    )
  }, [dailyTasks])

  const wasTaskHandledByAdmin = (task) => {
    return task?.createdByAdmin || task?.editedByAdmin
  }
  const taskTemplate = (item) => {
    const textColor = theme === "light" ? "clr-secondary" : ""
    const isSelected = selectedTasks.some((task) => task.taskID === item.taskID)

    // MIssing elipsis
    const category = item.specialCategory || item.category
    const module = item.module

    const subtitle = `${module || ""}${module && category ? " | " : ""}${category || ""}`

    return (
      <div
        className={`flex align-items-center justify-content-flex-start task-wrapper h-fit py-2 ${isSelected ? "p-datatable-cell-selected" : ""}`}
      >
        <div className="w-full flex align-items-center task-data gap-2">
          <ClientProjectChip
            clientCode={item.clientCode}
            projectCode={item.projectCode}
          />

          <div style={{ display: "flex", minWidth: 0 }}>
            <span
              style={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
              className="task-title"
            >
              <p className="task-name" title={item.task}>
                {item.task}
              </p>
              <span className={`task-module ${textColor}`}>{subtitle}</span>
            </span>
          </div>
          <div className="task-data-chips flex flex-1 gap-2 align-items-center justify-content-end ml-auto">
            {wasTaskHandledByAdmin(item) ?
              <TaskAdminInteractionChip
                state={
                  item.editedByAdmin ?
                    TASK_ADMIN_INTERACTION_CHIP_TYPE.EDITION
                  : TASK_ADMIN_INTERACTION_CHIP_TYPE.CREATION
                }
                name={item.adminName + " " + item.adminLastName}
              />
            : null}
            {item?.comment && (
              <Chip
                className={`comment-badge h-full p-chip-text justify-content-center p-0 mx-1 comment-bubble-${item?.taskID}`}
                icon={`pi pi-comment p-chip-text justify-content-center p-0 m-0 text-xs comment-${item?.taskID}`}
              />
            )}
            {item?.comment && (
              <Tooltip
                target={`.comment-bubble-${item?.taskID}`}
                position="bottom"
                content={item?.comment}
              />
            )}
          </div>
          <div className="flex gap-2 align-items-center justify-content-end">
            <div className={`flex justify-content-between gap-3 ${textColor}`}>
              {item.sisuTime !== item.billedTime && (
                <>
                  <Tooltip
                    target={`#diff-chip-${item.taskOrder}`}
                    position="bottom"
                  >
                    <strong>Motivo de la diferencia:</strong>
                    <p>{item.timeDifferenceComment}</p>
                  </Tooltip>
                  <span
                    id={`diff-chip-${item.taskOrder}`}
                    className="select-none flex gap-1 align-items-center difference-reason-chip"
                  >
                    <i
                      className="pi pi-dollar"
                      style={{ color: "var(--color-text)" }}
                    />
                    {formatHours(item.billedTime)}
                  </span>
                </>
              )}
              <span className="select-none flex gap-1 align-items-center">
                <i
                  className="pi pi-stopwatch"
                  style={{ color: "var(--color-text)" }}
                />
                {formatHours(item.sisuTime)}
              </span>
            </div>
            {isDayEditable ?
              <i
                className="pi pi-ellipsis-v text-xl cursor-pointer px-2"
                onClick={(event) => {
                  if (isSelectingTasks) {
                    multiSelectCMRef?.current.show(event)
                  } else {
                    cm.current.show(event)
                    setSelectedTask(item)
                  }
                }}
              />
            : null}
          </div>
        </div>
      </div>
    )
  }
  const editableDayEmptyTaskTemplate = () => {
    return (
      <div className="empty-tasks-wrapper w-full">
        <div className="flex flex-column gap-2 align-items-center justify-content-center max-w-25rem m-auto text-center">
          <i className="pi pi-folder-open text-3xl" />
          <span className="text-xl font-semibold">Nada por aquí (todavía)</span>
          <span>
            Pequeños pasos, grandes avances y tus mejores victorias ¡Registra
            todas tus tareas del día!
          </span>
          <PrimaryButton
            label={"¡Añade una tarea!"}
            icon={"pi pi-plus"}
            disabled={!isDayEditable}
            className="w-fit"
            onClick={onClickBeginEditingNewTaskName}
          />
        </div>
      </div>
    )
  }

  const nonEditableEmptyTaskTemplate = () => {
    return (
      <div className="empty-tasks-wrapper w-full">
        <div className="flex flex-column gap-2 align-items-center justify-content-center max-w-25rem m-auto text-center">
          <i className="pi pi-folder-open text-3xl" />
          <span className="text-xl font-semibold">
            ¡Día libre!... de tareas
          </span>
          <span>
            Si no es lo que esperabas, contacta al administrador para que te
            ayude a registrar tus tareas.
          </span>
          {isDayEditable && (
            <PrimaryButton
              label={"¡Añade una tarea!"}
              icon={"pi pi-plus"}
              disabled={!isDayEditable}
              className="w-fit"
              onClick={onClickBeginEditingNewTaskName}
            />
          )}
        </div>
      </div>
    )
  }

  const vacationDayTemplate = () => {
    return (
      <div className="empty-tasks-wrapper custom-empty-message vacation-day w-full">
        <div className="flex flex-column gap-2 align-items-center justify-content-center max-w-25rem m-auto text-center">
          <BeachAccessOutlinedIcon fontSize="large" className="clr-text" />
          <h3 className="message-title font-cairo text-xl font-semibold mt-2 mb-1">
            ¿Qué haces aquí?
          </h3>
          <span className="message-content font-mulish max-w-25rem">
            Este es un día libre. Si decides registrar tareas, queda claro que
            el descanso no es lo tuyo.
          </span>
        </div>
      </div>
    )
  }

  const loadingTasksTemplate = () => {
    return (
      <div className="empty-tasks-wrapper w-full">
        <div className="flex flex-column gap-2 align-items-center justify-content-center max-w-25rem m-auto">
          <i className="pi pi-spin pi-spinner text-3xl" />
          <span className="text-xl font-semibold">Cargando tareas...</span>
        </div>
      </div>
    )
  }

  const renderEmptyTaskTemplate = () => {
    if (loading) return null
    if (dailyTasks?.length > 0) return null
    if (
      isDayEditable &&
      isSameDay(getDateTime().toJSDate(), selectedDate) &&
      !daysOff?.some((day) => isSameDay(day, selectedDate))
    ) {
      return editableDayEmptyTaskTemplate()
    } else if (daysOff?.some((day) => isSameDay(day, selectedDate))) {
      return vacationDayTemplate()
    } else {
      return nonEditableEmptyTaskTemplate()
    }
  }

  return (
    <>
      {dailyTasks && dailyTasks.length > 0 && !loading && (
        <div className="card w-full p-0">
          <DataTable
            className={clsx(
              "today-tasks-list",
              isSelectingTasks && "is-multiselect"
            )}
            value={dailyTasks}
            reorderableRows={
              dailyTasks.length !== 1 && selectedTasks.length === 0
            }
            onRowReorder={handleItemDrop}
            responsiveLayout="scroll"
            noSameWidthColumns
            noFixedColumns
            onContextMenu={
              !isDayEditable ? undefined : (
                ({ originalEvent, data }) => {
                  if (isSelectingTasks) {
                    multiSelectCMRef?.current.show(originalEvent)
                  } else {
                    cm?.current.show(originalEvent)
                    setSelectedTask(data)
                  }
                }
              )
            }
            onRowClick={({ data }) => {
              if (isSelectingTasks) onClickSelectCheckbox(data)
            }}
          >
            {isSelectingTasks ?
              <Column
                style={{ width: "1rem", padding: "7px" }}
                body={(rowData) => {
                  const checkboxId = `checkbox-${rowData.taskID}`
                  return (
                    <InputCheckbox
                      inputId={checkboxId}
                      checked={selectedTasks.some(
                        (task) => task.taskID === rowData.taskID
                      )}
                    />
                  )
                }}
              />
            : isDayEditable && (
                <Column
                  style={{
                    width: "1rem",
                    padding: "7px",
                    justifyContent: "center",
                  }}
                  className={`${dailyTasks.length === 1 ? "non-reorderable" : ""}`}
                  rowReorder
                  rowReorderIcon="pi pi-pause rotate-90 text-lg"
                />
              )
            }
            <Column
              body={taskTemplate}
              style={{ padding: "7px 7px 7px 0px", width: "100%" }}
              className={clsx(
                isSelectingTasks && "p-datatable-cell-multiselect-content"
              )}
            />
          </DataTable>
        </div>
      )}
      {loading && loadingTasksTemplate()}
      {renderEmptyTaskTemplate()}
    </>
  )
}

export default TaskList
